/**
 * PersonaChip · chip reusable que resuelve Usuario.id -> Persona slim (0677 F3 piloto #4).
 *
 * Reemplaza el patron `subidoPor.slice(0, 8)` de AdjuntosSection (0674) y ComentariosSection (0675).
 * Reusable en futuro WorkflowTimeline + VersionTimeline + DelegarModal.
 *
 * Estados:
 *   - loading: skeleton animado
 *   - success: avatar inicials (color hash) + nombre completo + tooltip con puesto + email
 *   - error/404: fallback UUID corto + tooltip "Persona no encontrada"
 */

import { useI18n } from '../../../i18n';
import {
  formatNombreCompleto,
  getColorAvatar,
  getIniciales,
  usePersona,
} from '../hooks/usePersona';
import styles from './PersonaChip.module.css';

export interface PersonaChipProps {
  userId: string;
  /** Si true, oculta el nombre y solo muestra avatar (modo compacto). Default false. */
  soloAvatar?: boolean;
}

export function PersonaChip({ userId, soloAvatar = false }: PersonaChipProps) {
  const { t } = useI18n();
  const tt = t.common.persona;
  const query = usePersona(userId);

  if (query.isLoading) {
    return (
      <span className={styles.skeleton} aria-label={tt.cargando}>
        <span className={styles.skeletonAvatar} />
        {!soloAvatar && <span className={styles.skeletonNombre} />}
      </span>
    );
  }

  if (query.isError || !query.data) {
    const short = userId.slice(0, 8);
    return (
      <span className={styles.fallback} title={tt.noEncontrada}>
        <span className={styles.fallbackAvatar} aria-hidden="true">
          ?
        </span>
        {!soloAvatar && <span>{short}</span>}
      </span>
    );
  }

  const p = query.data;
  const nombreCompleto = formatNombreCompleto(p);
  const iniciales = getIniciales(p);
  const color = getColorAvatar(userId);
  const tooltip = p.puestoTitulo
    ? `${nombreCompleto} · ${p.puestoTitulo} · ${p.email}`
    : `${nombreCompleto} · ${tt.puestoUnknown} · ${p.email}`;

  return (
    <span className={styles.chip} title={tooltip}>
      <span className={styles.avatar} style={{ background: color }} aria-hidden="true">
        {iniciales}
      </span>
      {!soloAvatar && <span className={styles.nombre}>{nombreCompleto}</span>}
    </span>
  );
}
